import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getEvent, addEvent, updateEvent, clearCurrentEvent } from '../actions/eventActions';
import { getPrograms } from '../actions/programActions';
import EventForm from '../components/content-management/EventForm';
import ContentManagementSidebar from '../components/content-management/ContentManagementSidebar';
import EventFormSkeleton from '../components/content-management/views/FormView/EventFormSkeleton';
import { X } from 'lucide-react';
import { isAdminRole } from '../utils/roleHelpers';

const EventEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentEvent, eventLoading } = useSelector(state => state.event);
  const { programs } = useSelector(state => state.program);
  const { user } = useSelector(state => state.auth);

  // Determine mode based on URL
  const isViewMode = location.pathname.includes('/view');
  const isEditMode = location.pathname.includes('/edit');
  const isNewMode = !id;

  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';
  const isReadOnly = isInstructor || isViewMode;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Always load programs for the form
        await dispatch(getPrograms());

        // If we have an ID, load the event
        if (id) {
          await dispatch(getEvent(id));
        }
      } catch (error) {
        console.error('Error loading event:', error);
      }
    };

    loadData();

    // Cleanup on unmount
    return () => {
      dispatch(clearCurrentEvent());
    };
  }, [dispatch, id]);

  const handleSubmit = async (eventData) => {
    try {
      const action = id ? updateEvent(id, eventData) : addEvent(eventData);
      await dispatch(action);
      // Redirect back to events list
      navigate('/content-management/events');
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleClose = () => {
    navigate('/content-management/events');
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Events & Information') {
      navigate('/content-management/events');
    } else {
      navigate('/content-management', { state: { activeTab: tabId } });
    }
  };

  // Show loading state
  if (eventLoading && id) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Events & Information"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="h-8 w-48 bg-gray-200 rounded animate-shimmer"></div>
                <button
                  type="button"
                  onClick={() => navigate('/content-management/events')}
                  className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Form Skeleton */}
              <EventFormSkeleton />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If trying to edit/view an event that doesn't exist
  if (id && !currentEvent && !eventLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Events & Information"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0">
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-red-500 text-xl mb-4">Event not found</div>
              <button
                onClick={() => navigate('/content-management/events')}
                className="px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
              >
                Return to Events
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mode = isViewMode ? 'view' : isEditMode ? 'edit' : 'new';

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Events"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          <EventForm
            mode={mode}
            event={currentEvent}
            programs={programs}
            userRole={user?.role}
            onSubmit={handleSubmit}
            onClose={handleClose}
          />
        </div>
      </div>
    </div>
  );
};

export default EventEditor;
